import { useCallback, useEffect, useMemo } from 'react';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useCompanyInstalledAppsStore } from 'stores/companyInstalledAppsStore';

/**
 * Shared company installed-apps state. All callers (Sidebar, Apps Store,
 * CompanyGuard, etc.) read the same Zustand store so install/uninstall
 * updates the sidebar immediately without a page refresh.
 */
export function useCompanyInstalledApps() {
  const companyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const installedAppIds = useCompanyInstalledAppsStore(
    (s) => s.installedAppIds,
  );
  const status = useCompanyInstalledAppsStore((s) => s.status);
  const loadError = useCompanyInstalledAppsStore((s) => s.loadError);
  const mutatingAppId = useCompanyInstalledAppsStore((s) => s.mutatingAppId);
  const mutatingMode = useCompanyInstalledAppsStore((s) => s.mutatingMode);
  const ensureLoaded = useCompanyInstalledAppsStore((s) => s.ensureLoaded);
  const reloadStore = useCompanyInstalledAppsStore((s) => s.reload);
  const installAppStore = useCompanyInstalledAppsStore((s) => s.installApp);
  const uninstallAppStore = useCompanyInstalledAppsStore((s) => s.uninstallApp);

  useEffect(() => {
    void ensureLoaded(companyId);
  }, [companyId, ensureLoaded]);

  const installedSet = useMemo(
    () => new Set(installedAppIds),
    [installedAppIds],
  );

  const isAppInstalled = useCallback(
    (appId: string) => installedSet.has(appId),
    [installedSet],
  );

  const reload = useCallback(async () => {
    await reloadStore(companyId);
  }, [companyId, reloadStore]);

  const installApp = useCallback(
    async (appId: string) => {
      if (!companyId) {
        return;
      }
      await installAppStore(companyId, appId);
    },
    [companyId, installAppStore],
  );

  const uninstallApp = useCallback(
    async (appId: string) => {
      if (!companyId) {
        return;
      }
      await uninstallAppStore(companyId, appId);
    },
    [companyId, uninstallAppStore],
  );

  return {
    installedAppIds,
    installedSet,
    isAppInstalled,
    status,
    loadError,
    mutatingAppId,
    mutatingMode,
    reload,
    installApp,
    uninstallApp,
  };
}
